import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useRestaurants } from '@/hooks/useRestaurants';
import { useSelectedRestaurant } from '@/hooks/useSelectedRestaurant';
import {
  FileText,
  ExternalLink,
  Calendar,
  Building2,
  Search,
  Filter,
  Eye,
} from 'lucide-react';

// Rapports statiques connus dans public/
// À terme, cette liste viendra de la table client_reports dans Supabase
const STATIC_REPORTS = [
  {
    id: '1',
    restaurant_slug: 'cafe-max',
    restaurant_name: 'Café Max',
    title: 'Rapport de Performances',
    type: 'performance' as const,
    file_path: '/rapport-cafemax.html',
    description: 'Rapport SEO, Search Console, GBP et GEO — Décembre 2025 à Février 2026',
    period: 'Déc. 2025 — Fév. 2026',
    created_at: '2026-02-28',
  },
  {
    id: '2',
    restaurant_slug: null,
    restaurant_name: 'À Côté',
    title: 'Pré-Audit Stratégique',
    type: 'pre_audit' as const,
    file_path: '/pre-audit-a-cote.html',
    description: 'Pré-audit SEO & GEO pour À Côté, Restaurant Viande d\'Exception Paris 9e',
    period: null,
    created_at: '2026-01-15',
  },
  {
    id: '3',
    restaurant_slug: null,
    restaurant_name: 'À Côté',
    title: 'Proposition d\'accompagnement',
    type: 'proposal' as const,
    file_path: '/accompagnement-getup-acote.html',
    description: 'Proposition d\'accompagnement Getup Agency x À Côté',
    period: null,
    created_at: '2026-01-20',
  },
  {
    id: '4',
    restaurant_slug: null,
    restaurant_name: 'À Côté',
    title: 'Devis N°2026542',
    type: 'quote' as const,
    file_path: '/devis-getup-acote.html',
    description: 'Devis détaillé pour l\'accompagnement À Côté',
    period: null,
    created_at: '2026-01-20',
  },
  {
    id: '5',
    restaurant_slug: null,
    restaurant_name: 'Eden Ecolodge',
    title: 'Pré-Audit Stratégique',
    type: 'pre_audit' as const,
    file_path: '/pre-audit-eden-ecolodge.html',
    description: 'Pré-audit SEO & GEO pour la Résidence Eden Ecolodge',
    period: null,
    created_at: '2025-12-10',
  },
  {
    id: '6',
    restaurant_slug: null,
    restaurant_name: 'Access Deco',
    title: 'Page d\'attente',
    type: 'other' as const,
    file_path: '/access-deco/index.html',
    description: 'Page d\'attente avec charte graphique Access Deco',
    period: null,
    created_at: '2026-03-05',
  },
  {
    id: '7',
    restaurant_slug: null,
    restaurant_name: 'Getup Agency',
    title: 'Entretien Traffic Manager',
    type: 'other' as const,
    file_path: '/entretien-traffic-manager.html',
    description: 'Grille d\'entretien pour recrutement Traffic Manager',
    period: null,
    created_at: '2026-03-01',
  },
];

const TYPE_LABELS: Record<string, { label: string; color: string; bg: string }> = {
  pre_audit: { label: 'Pré-Audit', color: 'text-blue-400', bg: 'bg-blue-400/10' },
  performance: { label: 'Performance', color: 'text-emerald-400', bg: 'bg-emerald-400/10' },
  monthly: { label: 'Mensuel', color: 'text-purple-400', bg: 'bg-purple-400/10' },
  proposal: { label: 'Proposition', color: 'text-amber-400', bg: 'bg-amber-400/10' },
  contract: { label: 'Contrat', color: 'text-rose-400', bg: 'bg-rose-400/10' },
  quote: { label: 'Devis', color: 'text-orange-400', bg: 'bg-orange-400/10' },
  other: { label: 'Autre', color: 'text-gray-400', bg: 'bg-gray-400/10' },
};

export const ReportsPage: React.FC = () => {
  const [filterType, setFilterType] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredReports = STATIC_REPORTS.filter((r) => {
    if (filterType !== 'all' && r.type !== filterType) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        r.title.toLowerCase().includes(q) ||
        r.restaurant_name.toLowerCase().includes(q) ||
        (r.description && r.description.toLowerCase().includes(q))
      );
    }
    return true;
  }).sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  const typeOptions = ['all', ...new Set(STATIC_REPORTS.map((r) => r.type))];

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-3xl font-bold text-gray-900">
          Rapports & Documents
        </h1>
        <p className="text-gray-500 mt-1">
          Tous les livrables générés pour vos clients
        </p>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="flex flex-col sm:flex-row gap-3"
      >
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher un rapport..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-stratos-gold/30 focus:border-stratos-gold"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {typeOptions.map((type) => {
            const info = type === 'all' ? { label: 'Tous', color: 'text-gray-600', bg: 'bg-gray-100' } : TYPE_LABELS[type];
            return (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                  filterType === type
                    ? 'bg-gray-900 text-white shadow-md'
                    : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                {info?.label || type}
              </button>
            );
          })}
        </div>
      </motion.div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total', value: STATIC_REPORTS.length, color: 'border-gray-200' },
          { label: 'Pré-Audits', value: STATIC_REPORTS.filter(r => r.type === 'pre_audit').length, color: 'border-blue-200' },
          { label: 'Performance', value: STATIC_REPORTS.filter(r => r.type === 'performance').length, color: 'border-emerald-200' },
          { label: 'Commerciaux', value: STATIC_REPORTS.filter(r => r.type === 'proposal' || r.type === 'quote').length, color: 'border-amber-200' },
        ].map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 + i * 0.05 }}
            className={`bg-white rounded-xl border-2 ${stat.color} p-4 text-center`}
          >
            <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
            <div className="text-xs text-gray-500 mt-1">{stat.label}</div>
          </motion.div>
        ))}
      </div>

      {/* Reports grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filteredReports.map((report, index) => {
          const typeInfo = TYPE_LABELS[report.type];
          return (
            <motion.div
              key={report.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + index * 0.05 }}
              className="bg-white rounded-xl border border-gray-200 hover:border-stratos-gold/40 hover:shadow-lg transition-all duration-300 overflow-hidden group"
            >
              {/* Type bar */}
              <div className={`h-1 ${typeInfo?.bg?.replace('/10', '/40') || 'bg-gray-300'}`} />

              <div className="p-5 space-y-3">
                {/* Header */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded ${typeInfo?.bg} ${typeInfo?.color}`}>
                        {typeInfo?.label}
                      </span>
                    </div>
                    <h3 className="font-semibold text-gray-900 truncate">
                      {report.title}
                    </h3>
                  </div>
                  <a
                    href={report.file_path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 rounded-lg text-gray-400 hover:text-stratos-gold hover:bg-stratos-gold/10 transition-colors flex-shrink-0"
                    title="Ouvrir dans un nouvel onglet"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>

                {/* Client */}
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Building2 className="w-3.5 h-3.5 text-gray-400" />
                  <span>{report.restaurant_name}</span>
                </div>

                {/* Description */}
                {report.description && (
                  <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">
                    {report.description}
                  </p>
                )}

                {/* Footer */}
                <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                  <div className="flex items-center gap-1.5 text-xs text-gray-400">
                    <Calendar className="w-3 h-3" />
                    <span>{new Date(report.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                  </div>
                  {report.period && (
                    <span className="text-[10px] text-gray-400 bg-gray-50 px-2 py-0.5 rounded">
                      {report.period}
                    </span>
                  )}
                </div>

                {/* Action */}
                <a
                  href={report.file_path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-2.5 rounded-lg bg-gray-50 hover:bg-gray-900 text-gray-600 hover:text-white text-sm font-medium transition-all duration-200 group-hover:bg-gray-900 group-hover:text-white"
                >
                  <Eye className="w-4 h-4" />
                  Consulter
                </a>
              </div>
            </motion.div>
          );
        })}
      </div>

      {filteredReports.length === 0 && (
        <div className="text-center py-16">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Aucun rapport ne correspond à votre recherche</p>
        </div>
      )}
    </div>
  );
};
